import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Badge,
  Button,
  Card,
  FileButton,
  Grid,
  Group,
  Loader,
  Select,
  Stack,
  Stepper,
  Tabs,
  Text,
  Title,
} from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import { IconCheck, IconDownload, IconUpload } from "@tabler/icons";
import { useServerApi } from "../../hooks/userServerApi";
import { downloadEntityTemplate } from "../../helpers/migrationTemplateDownload";
import {
  formatImportSummary,
  getImportErrorMessage,
  hasImportWarnings,
} from "../../helpers/importResultUtils";
import MigrationBackLink from "./MigrationBackLink";
import {
  CUSTOM_MIGRATION_SECTIONS,
  stepsForSection,
} from "./customMigrationSteps";

const CustomDataMigrationWizard = () => {
  const { t } = useTranslation();
  const [api] = useServerApi();
  const [sectionId, setSectionId] = useState(CUSTOM_MIGRATION_SECTIONS[0].id);
  const [activeStep, setActiveStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [file, setFile] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [importMode, setImportMode] = useState("insert");
  const [bankAccountId, setBankAccountId] = useState(null);
  const [bankAccounts, setBankAccounts] = useState([]);

  const steps = stepsForSection(sectionId);
  const currentStep = steps[activeStep];

  const resetStepState = () => {
    setFile(null);
    setImportResult(null);
    setImportMode("insert");
    setBankAccountId(null);
  };

  const handleSectionChange = (nextSection) => {
    if (!nextSection || nextSection === sectionId) return;
    setSectionId(nextSection);
    setActiveStep(0);
    resetStepState();
  };

  const handleStepSelect = (index) => {
    setActiveStep(index);
    resetStepState();
  };

  useEffect(() => {
    if (!currentStep?.requiresBankAccount) return undefined;
    let cancelled = false;

    api
      .search({
        apiEntity: "bankAccount",
        pageSize: 200,
        currentPage: 1,
        sort: { by: "name", order: "asc" },
        select: "name code accountNumber",
      })
      .then((data) => {
        if (!cancelled) setBankAccounts(data?.docs || []);
      })
      .catch(() => {
        if (!cancelled) setBankAccounts([]);
      });

    return () => {
      cancelled = true;
    };
  }, [currentStep?.id, currentStep?.requiresBankAccount]);

  const downloadTemplate = async (item) => {
    if (!item) return;
    setLoading(true);
    try {
      const url = `${process.env.REACT_APP_SERVER_URL}/${item.path}`;
      await downloadEntityTemplate(url, item.fileName);
      showNotification({
        title: t("Template downloaded"),
        message: t(item.text),
      });
    } catch (error) {
      showNotification({
        color: "red",
        title: t("Download failed"),
        message: error.message,
      });
    } finally {
      setLoading(false);
    }
  };

  const runImport = async () => {
    if (!file || !currentStep) return;
    if (currentStep.requiresBankAccount && !bankAccountId) return;
    setLoading(true);
    try {
      const importData = {
        ...(currentStep.importData ?? {}),
        ...(currentStep.importModes?.length ? { importMode } : {}),
        ...(currentStep.requiresBankAccount ? { bankAccountId } : {}),
      };

      let result;
      if (currentStep.importKind === "policyInsured") {
        result = await api.Order.importPolicyInsuredBatch(file);
      } else if (currentStep.importKind === "policyBeneficiary") {
        result = await api.Order.importPolicyBeneficiaryBatch(file);
      } else if (currentStep.importKind === "providerLogin") {
        result = await api.Provider.importAllLoginBatch(file, importData);
      } else if (currentStep.importKind === "providerContact") {
        result = await api.Provider.importAllContactBatch(file, importData);
      } else {
        result = await api.importBatch({
          apiEntity: currentStep.apiEntity,
          file,
          importData,
        });
      }

      if (result && result.success === false) {
        throw result.error ?? { message: result.message ?? "Import failed" };
      }

      const summary = result?.data ?? result;
      setImportResult(summary);
      setCompletedSteps((prev) => [...new Set([...prev, currentStep.id])]);
      showNotification({
        color: hasImportWarnings(summary) ? "yellow" : "green",
        title: hasImportWarnings(summary)
          ? t("Import completed with warnings")
          : t("Import completed"),
        message: formatImportSummary(summary),
      });
    } catch (error) {
      showNotification({
        color: "red",
        title: t("Import failed"),
        message: getImportErrorMessage(error),
      });
    } finally {
      setLoading(false);
    }
  };

  const importDisabled =
    !file ||
    loading ||
    (currentStep?.requiresBankAccount && !bankAccountId);

  return (
    <Stack spacing="md">
      <MigrationBackLink />
      <Title order={3}>{t("Custom Data Migration")}</Title>
      <Text size="sm" color="dimmed">
        {t(
          "Download MBID import templates, fill in your data, then import each record. Main records follow dependency order. Other records add the detail files used on the list pages."
        )}
      </Text>

      <Tabs value={sectionId} onTabChange={handleSectionChange}>
        <Tabs.List>
          {CUSTOM_MIGRATION_SECTIONS.map((section) => (
            <Tabs.Tab key={section.id} value={section.id}>
              {t(section.label)}
            </Tabs.Tab>
          ))}
        </Tabs.List>
      </Tabs>

      <Grid gutter="lg">
        <Grid.Col span={12} md={3}>
          <Stepper
            active={activeStep}
            onStepClick={handleStepSelect}
            orientation="vertical"
            breakpoint="sm"
          >
            {steps.map((step, index) => (
              <Stepper.Step
                key={step.id}
                label={t(step.label)}
                description={t(step.behavior)}
                icon={
                  completedSteps.includes(step.id) ? (
                    <IconCheck size={16} />
                  ) : (
                    index + 1
                  )
                }
              />
            ))}
          </Stepper>
        </Grid.Col>

        <Grid.Col span={12} md={9}>
          {currentStep ? (
            <Card withBorder padding="lg">
              <Stack spacing="sm">
                <Group>
                  <Title order={4}>{t(currentStep.label)}</Title>
                  <Badge>{t(currentStep.behavior)}</Badge>
                </Group>
                <Text size="sm">{t(currentStep.description)}</Text>

                {currentStep.importModes?.length > 0 && (
                  <Select
                    label={t("Import mode")}
                    data={currentStep.importModes.map((mode) => ({
                      value: mode.value,
                      label: t(mode.label),
                    }))}
                    value={importMode}
                    onChange={(value) => setImportMode(value || "insert")}
                  />
                )}

                {currentStep.requiresBankAccount && (
                  <Select
                    label={t("Bank account")}
                    placeholder={t("Select a bank account")}
                    data={bankAccounts.map((account) => ({
                      value: account._id,
                      label:
                        [account.code, account.name || account.accountNumber]
                          .filter(Boolean)
                          .join(" - ") || account._id,
                    }))}
                    value={bankAccountId}
                    onChange={setBankAccountId}
                    searchable
                  />
                )}

                <Group>
                  {currentStep.templates.map((item) => (
                    <Button
                      key={item.path}
                      disabled={loading}
                      color="blue"
                      variant={
                        item.text.startsWith("Download Existing")
                          ? "outline"
                          : "filled"
                      }
                      leftIcon={<IconDownload size={16} />}
                      onClick={() => downloadTemplate(item)}
                    >
                      {t(item.text)}
                    </Button>
                  ))}
                  <FileButton onChange={setFile} accept=".xls,.xlsx">
                    {(props) => (
                      <Button {...props} leftIcon={<IconUpload size={16} />}>
                        {file ? file.name : t("Upload filled template")}
                      </Button>
                    )}
                  </FileButton>
                  <Button
                    disabled={importDisabled}
                    color="green"
                    onClick={runImport}
                  >
                    {t("Import this step")}
                  </Button>
                </Group>

                {loading && <Loader size="sm" />}

                {importResult && (
                  <Alert
                    color={hasImportWarnings(importResult) ? "yellow" : "green"}
                    title={t("Import result")}
                  >
                    {formatImportSummary(importResult)}
                  </Alert>
                )}
              </Stack>
            </Card>
          ) : null}
        </Grid.Col>
      </Grid>
    </Stack>
  );
};

export default CustomDataMigrationWizard;
